import { format, fromUnixTime } from "date-fns/esm";
import { useNavigate } from "react-router";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import TileContent from "ds/components/Tile/Content";
import TileWrapper from "ds/components/Tile/Wrapper";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import { Clock, TickCircleFilled } from "components/icons/generated";
import { Certificate } from "shared/UserGuides/useUserGuideCertificates";
import { showGenerateCertificateModal } from "views/Account/LaunchPad/UserGuidesGroup/SidePanel/GenerateCertificateModal";
import badgeLockedSrc from "shared/UserGuides/assets/badge-locked.svg";
import badgeUnlockedSrc from "shared/UserGuides/assets/badge-unlocked.svg";

import styles from "./styles.module.css";

type LaunchPadLearnCertificateTileLockedProps = {
  certificate?: never;
  groupId: string;
  groupName: string;
  timeToComplete: string;
  allGuidesCompleted: boolean;
  guidesCount: number;
};

type LaunchPadLearnCertificateTileEarnedProps = {
  certificate: Certificate;
  groupId: string;
  groupName: string;
  timeToComplete?: never;
  allGuidesCompleted?: never;
};

type LaunchPadLearnCertificateTileProps =
  | LaunchPadLearnCertificateTileLockedProps
  | LaunchPadLearnCertificateTileEarnedProps;

const getStatusText = (
  certificate: Certificate | undefined,
  allGuidesCompleted: boolean,
  isComingSoon: boolean,
  timeToComplete?: string
) => {
  if (certificate) {
    return `Issued on ${format(fromUnixTime(certificate.issuedAt), "MM/dd/yyyy")}`;
  }

  if (allGuidesCompleted) return "Complete";
  if (isComingSoon) return "Coming soon";

  return `Complete in ${timeToComplete}`;
};

const getStatusTooltipText = (
  certificate: Certificate | undefined,
  allGuidesCompleted: boolean,
  isComingSoon: boolean,
  timeToComplete?: string
) => {
  if (isComingSoon || allGuidesCompleted || certificate) return undefined;

  return `Estimated time to complete: ${timeToComplete}`;
};

const getTooltipText = (isComingSoon: boolean) => {
  if (isComingSoon) return "This mission is coming soon";

  return "Complete all mission objectives to unlock your certificate.";
};

const LaunchPadLearnCertificateTile = (props: LaunchPadLearnCertificateTileProps) => {
  const navigate = useNavigate();
  const { groupId, groupName, certificate } = props;

  const isEarned = !!certificate;
  const allGuidesCompleted = !isEarned && props.allGuidesCompleted;
  const isComingSoon = !isEarned && props.guidesCount === 0;
  const isLocked = !isEarned && !allGuidesCompleted;

  const badgeSrc = isEarned ? badgeUnlockedSrc : badgeLockedSrc;
  const title = isEarned ? certificate.course : groupName;
  const statusIcon = isLocked ? Clock : TickCircleFilled;
  const statusText = getStatusText(
    certificate,
    allGuidesCompleted,
    isComingSoon,
    props.timeToComplete
  );
  const statusTooltipText = getStatusTooltipText(
    certificate,
    allGuidesCompleted,
    isComingSoon,
    props.timeToComplete
  );

  const handleClick = isEarned
    ? () => navigate(`/mission-control?certificate=${certificate.id}`)
    : undefined;

  const getClassName = () => {
    if (isLocked) return styles.lockedTile;
    if (allGuidesCompleted) return styles.readyTile;

    return undefined;
  };

  const className = getClassName();

  const tileContent = (
    <>
      {allGuidesCompleted && (
        <Box className={styles.unlockButton}>
          <Button
            variant="secondary"
            size="small"
            onPress={() => showGenerateCertificateModal({ groupId })}
          >
            Unlock
          </Button>
        </Box>
      )}
      <TileContent gap="large">
        <img src={badgeSrc} alt="" className={styles.badge} aria-hidden />
        <Box direction="column" gap="small">
          <Typography variant="p-t7" tag="span">
            {title}
          </Typography>
          <Box direction="row" align="center" gap="small">
            <Icon src={statusIcon} color="secondary" />
            <Tooltip
              active={!!statusTooltipText}
              on={(props) => (
                <Typography {...props} variant="p-body3" tag="span" color="secondary">
                  {statusText}
                </Typography>
              )}
            >
              {statusTooltipText}
            </Tooltip>
          </Box>
        </Box>
      </TileContent>
    </>
  );

  if (isLocked) {
    return (
      <Tooltip
        on={(tooltipProps) => (
          <TileWrapper {...tooltipProps} className={className}>
            {tileContent}
          </TileWrapper>
        )}
        widthMode="maxWidthSm"
      >
        {getTooltipText(isComingSoon)}
      </Tooltip>
    );
  }

  return (
    <TileWrapper onClick={handleClick} className={className}>
      {tileContent}
    </TileWrapper>
  );
};

export default LaunchPadLearnCertificateTile;
